import { useEffect, useState, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';

import { useHwcgContactLists } from 'hooks/hwcgContactLists/hwcgContactListHooks';
import {
  eocLocationForCompany,
  officeLocationForCompany,
  useHwcgMemberCompanies,
} from 'hooks/hwcgMemberCompanies/hwcgMemberCompanyHooks';
import { useHwcgFlag } from 'utils/featureFlags';

import {
  hwcgNoticeErrors,
  type CreateIncidentFormValues,
} from './createIncidentModel';
import { HwcgAuthorizationSection } from './HwcgAuthorizationSection';
import { HwcgEocActionPanel } from './HwcgEocActionPanel';
import { HwcgGroupActionsEditor } from './HwcgGroupActionsEditor';
import { createEmptyHwcgAuthorization } from './hwcgAuthorization';
import {
  hwcgEocCompanyMatches,
  createEmptyHwcgEocNotice,
} from './hwcgEocNotice';
import {
  HWCG_GROUP_IDS,
  HWCG_GROUP_LABELS,
  type HwcgContactListId,
  type HwcgGroupId,
} from './hwcgGroupActions';

function addTargetFor(
  groupId: HwcgGroupId,
  companyName: string
): { constituency: HwcgContactListId; memberCompany: string } {
  if (groupId === 'eoc') {
    return { constituency: 'eoc', memberCompany: companyName.trim() };
  }
  if (groupId === 'responsible_party') {
    return { constituency: 'mutual_aid', memberCompany: companyName.trim() };
  }
  if (groupId === 'service_provider') {
    return { constituency: 'service_provider', memberCompany: '' };
  }
  return { constituency: 'mutual_aid', memberCompany: '' };
}
import {
  HWCG_COMPANY_OPTIONS,
  hwcgLocationSummary,
  parseHwcgLatLon,
  type HwcgIncidentSpecificsState,
} from './hwcgIncidentSpecifics';

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide"
    >
      {children} <span className="tw:text-destructive">*</span>
    </Label>
  );
}

/**
 * HWCG step 1. The call taker fills this in while still on the phone, so the
 * fields stay flat text and the incident name, point and description are
 * mirrored onto the real form fields as they are typed.
 */
export function StepHwcgIncidentSpecifics() {
  const { watch, setValue } = useFormContext<CreateIncidentFormValues>();
  const specifics = watch('hwcg_specifics');
  const groupActions = watch('hwcg_group_actions');
  const eocNotice = watch('hwcg_eoc') ?? createEmptyHwcgEocNotice();
  const groupNotices = watch('hwcg_notices');
  const authorization =
    watch('hwcg_authorization') ?? createEmptyHwcgAuthorization();
  const [selectedGroup, setSelectedGroup] = useState<HwcgGroupId>('eoc');
  const sponsor = watch('sponsor_organization');
  const hwcg = useHwcgFlag();
  const contactLists = useHwcgContactLists({
    enabled: hwcg,
    groupGuid: sponsor,
  });
  const memberCompanies = useHwcgMemberCompanies({
    enabled: hwcg,
    groupGuid: sponsor,
  });
  const lists = contactLists.data ?? [];
  const companyName = specifics?.companyName ?? '';

  const applyDirectoryLocationsForCompany = (nextCompany: string) => {
    const eocLocation = eocLocationForCompany(
      memberCompanies.data ?? [],
      nextCompany
    );
    if (eocLocation) {
      setValue(
        'hwcg_eoc',
        {
          ...(watch('hwcg_eoc') ?? createEmptyHwcgEocNotice()),
          locationLabel: eocLocation,
          location: null,
        },
        { shouldDirty: true }
      );
    }
    const officeLocation = officeLocationForCompany(
      memberCompanies.data ?? [],
      nextCompany
    );
    if (officeLocation) {
      const notices = watch('hwcg_notices') ?? {
        responsible_party: createEmptyHwcgEocNotice(),
        mutual_aid: createEmptyHwcgEocNotice(),
        service_provider: createEmptyHwcgEocNotice(),
      };
      setValue(
        'hwcg_notices',
        {
          ...notices,
          responsible_party: {
            ...(notices.responsible_party ?? createEmptyHwcgEocNotice()),
            locationLabel: officeLocation,
            location: null,
          },
        },
        { shouldDirty: true }
      );
    }
  };

  // Seed EOC + report-to locations once the directory loads for the current RP.
  useEffect(() => {
    if (!hwcg || !memberCompanies.data?.length) return;
    const currentEoc = watch('hwcg_eoc') ?? createEmptyHwcgEocNotice();
    const currentRp =
      watch('hwcg_notices')?.responsible_party ?? createEmptyHwcgEocNotice();
    if (currentEoc.locationLabel.trim() && currentRp.locationLabel.trim())
      return;
    applyDirectoryLocationsForCompany(companyName);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when directory/company first available
  }, [hwcg, memberCompanies.data, companyName]);
  const contacts = lists.flatMap((list) =>
    list.members.map((member) => ({
      ...member,
      constituency: (list.constituency === 'responsible_party'
        ? 'mutual_aid'
        : list.constituency) as HwcgContactListId,
    }))
  );
  const authorizedPeople = contacts.filter(
    (contact) => contact.authorizedIndividual === true
  );
  const mutualAidOthers = contacts.filter(
    (contact) =>
      contact.constituency === 'mutual_aid' &&
      !hwcgEocCompanyMatches(contact.memberCompany, companyName)
  );
  const serviceProviderContacts = contacts.filter(
    (contact) => contact.constituency === 'service_provider'
  );

  const patch = (partial: Partial<HwcgIncidentSpecificsState>) => {
    const next = { ...specifics, ...partial };
    setValue('hwcg_specifics', next, { shouldDirty: true });

    if (partial.incidentName !== undefined) {
      setValue('incident_name', partial.incidentName, { shouldDirty: true });
    }

    if (partial.latitude !== undefined || partial.longitude !== undefined) {
      const point = parseHwcgLatLon(next.latitude, next.longitude);
      setValue(
        'location',
        point ? { type: 'Point', coordinates: point } : null,
        { shouldDirty: true }
      );
      setValue('address', point ? hwcgLocationSummary(point) : null, {
        shouldDirty: true,
      });
    }
  };

  const textField = (
    key: keyof HwcgIncidentSpecificsState,
    id: string,
    label: string,
    options: { placeholder?: string; type?: string } = {}
  ) => (
    <div className="tw:grid tw:gap-1.5">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type={options.type}
        placeholder={options.placeholder}
        value={specifics[key]}
        onChange={(event) => patch({ [key]: event.target.value })}
      />
    </div>
  );

  return (
    <div
      className="tw:mx-auto tw:w-full tw:max-w-6xl tw:space-y-6"
      data-tour="create-incident-name"
    >
      <div>
        <h2 className="tw:text-lg tw:font-semibold tw:tracking-tight">
          Initiate Incident &amp; Obtain Incident Specifics
        </h2>
        <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
          Capture the Responsible Party, caller details, location, and well
          information before building the team.
        </p>
      </div>

      <div className="tw:grid tw:gap-4 tw:sm:grid-cols-2">
        <div className="tw:grid tw:gap-1.5 tw:sm:col-span-2">
          <FieldLabel htmlFor="hwcg-incident-name">Incident Name</FieldLabel>
          <Input
            id="hwcg-incident-name"
            value={specifics.incidentName}
            onChange={(event) => patch({ incidentName: event.target.value })}
            className="tw:h-11 tw:text-base"
          />
        </div>
        <div className="tw:grid tw:gap-1.5 tw:sm:col-span-2">
          <FieldLabel htmlFor="hwcg-company-name">Responsible Party</FieldLabel>
          <SearchableSelect
            id="hwcg-company-name"
            aria-label="Responsible Party"
            options={HWCG_COMPANY_OPTIONS.map((company) => ({
              value: company,
              label: company,
            }))}
            value={
              specifics.companyName
                ? { value: specifics.companyName, label: specifics.companyName }
                : null
            }
            placeholder="Select responsible party"
            contentClassName="dark"
            positionerClassName="tw:z-[60]"
            onChange={(next) => {
              const nextCompany = next?.value ?? '';
              patch({ companyName: nextCompany });
              applyDirectoryLocationsForCompany(nextCompany);
            }}
          />
        </div>
      </div>

      <section className="tw:space-y-3">
        <h3 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          1A · Caller &amp; Notification
        </h3>
        <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
          {textField('callerName', 'hwcg-caller-name', 'Caller Name', {
            placeholder: 'First and last name',
          })}
          {textField('callbackNumber', 'hwcg-callback', 'Callback #', {
            placeholder: '(___) ___-____',
          })}
          {textField('callerEmail', 'hwcg-caller-email', 'Caller Email', {
            type: 'email',
          })}
          {textField('date', 'hwcg-date', 'Date')}
          {textField('time', 'hwcg-time', 'Time')}
          {textField('callTaker', 'hwcg-call-taker', 'HWCG Call Taker', {
            placeholder: 'Name of HWCG call taker',
          })}
        </div>
      </section>

      <section className="tw:space-y-3">
        <h3 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          1B · Location &amp; Well
        </h3>
        <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
          {textField('latitude', 'hwcg-latitude', 'Latitude')}
          {textField('longitude', 'hwcg-longitude', 'Longitude')}
          {textField('blockNumber', 'hwcg-block', 'Block + #')}
          {textField('waterDepthFt', 'hwcg-water-depth', 'Water Depth (ft)')}
          {textField('wellName', 'hwcg-well-name', 'Well Name and #')}
          {textField('drillRigName', 'hwcg-drill-rig', 'Drill Rig Name', {
            placeholder: 'Rig name',
          })}
        </div>
      </section>

      <HwcgAuthorizationSection
        companyName={specifics?.companyName ?? ''}
        specificsDate={specifics?.date ?? ''}
        specificsTime={specifics?.time ?? ''}
        people={authorizedPeople}
        authorization={authorization}
        onChange={(next) =>
          setValue('hwcg_authorization', next, { shouldDirty: true })
        }
      />

      <div className="tw:grid tw:gap-1.5">
        <FieldLabel htmlFor="hwcg-description">Incident Description</FieldLabel>
        <Textarea
          id="hwcg-description"
          rows={5}
          placeholder="Nature of the event, current well status, actions taken to date..."
          value={specifics.description}
          onChange={(event) => patch({ description: event.target.value })}
        />
      </div>

      <section className="tw:rounded-lg tw:border tw:p-4">
        <HwcgGroupActionsEditor
          actions={groupActions}
          selectedId={selectedGroup}
          onSelect={setSelectedGroup}
          onChange={(next) =>
            setValue('hwcg_group_actions', next, { shouldDirty: true })
          }
          issues={Object.fromEntries(
            HWCG_GROUP_IDS.map((groupId) => {
              const action = groupActions[groupId];
              if (!action) return [groupId, 'Choose an action.'];
              const notice =
                groupId === 'eoc'
                  ? eocNotice
                  : (groupNotices?.[groupId] ?? createEmptyHwcgEocNotice());
              return [groupId, hwcgNoticeErrors(groupId, action, notice)[0]];
            })
          )}
          noticePanel={
            <HwcgEocActionPanel
              groupId={selectedGroup}
              groupLabel={HWCG_GROUP_LABELS[selectedGroup]}
              action={
                groupActions[selectedGroup] === 'notify' ? 'notify' : 'activate'
              }
              companyName={companyName}
              specifics={specifics}
              notice={
                selectedGroup === 'eoc'
                  ? eocNotice
                  : (groupNotices?.[selectedGroup] ??
                    createEmptyHwcgEocNotice())
              }
              contacts={
                selectedGroup === 'mutual_aid'
                  ? mutualAidOthers
                  : selectedGroup === 'service_provider'
                    ? serviceProviderContacts
                    : contacts
              }
              matchCompany={
                selectedGroup === 'eoc' || selectedGroup === 'responsible_party'
              }
              addTarget={addTargetFor(selectedGroup, companyName)}
              onChange={(next) => {
                if (selectedGroup === 'eoc') {
                  setValue('hwcg_eoc', next, { shouldDirty: true });
                  return;
                }
                const empty = createEmptyHwcgEocNotice();
                const notices: CreateIncidentFormValues['hwcg_notices'] = {
                  ['responsible_party']:
                    groupNotices?.responsible_party ?? empty,
                  ['mutual_aid']: groupNotices?.mutual_aid ?? empty,
                  ['service_provider']: groupNotices?.service_provider ?? empty,
                };
                notices[selectedGroup] = next;
                setValue('hwcg_notices', notices, { shouldDirty: true });
              }}
            />
          }
        />
      </section>
    </div>
  );
}
